import { useState, useEffect, useCallback } from 'react'
import { flaskApi } from '@/lib/flaskApi'
import { Button } from '@/components/ui/button'
import { useNotification } from '@refinedev/core'
import { Search, ChevronDown, Plus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'

interface Approver {
  id: number; userId: string; userName: string; status: string; createdAt: string | null
}
interface Chain {
  id: string; title: string; status: string; creatorId: string
  approvalCycleType: string; approvers: Approver[]
}
interface Company { id: string; name: string }
interface ChainGroup { company: Company; chains: Chain[] }

export function ApprovalChainPage() {
  const [groups, setGroups] = useState<ChainGroup[]>([])
  const [searchOpen, setSearchOpen] = useState(false)
  const [q, setQ] = useState('')
  const [co, setCo] = useState<{id:string,name:string}[]>([])
  const [loading, setLoading] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [addingFor, setAddingFor] = useState<string | null>(null)
  const [companyUsers, setCompanyUsers] = useState<{id:number,userId:string,name:string}[]>([])
  const { open: notify } = useNotification()

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await flaskApi.get('/approval-chains')
      const byCompany: Record<string, ChainGroup> = {}
      for (const item of (data || [])) {
        if (!item.companyId) continue
        if (!byCompany[item.companyId]) {
          byCompany[item.companyId] = { company: { id: item.companyId, name: item.companyName || item.companyId }, chains: [] }
        }
        byCompany[item.companyId].chains.push(item)
      }
      setGroups(Object.values(byCompany))
    } catch { notify?.({ type: 'error', message: 'Failed' }) }
    finally { setLoading(false) }
  }, [notify])

  const loadOne = useCallback(async (cid: string) => {
    setLoading(true)
    try {
      const { data } = await flaskApi.get('/approval-chains', { params: { company_id: cid } })
      const byCompany: Record<string, ChainGroup> = {}
      for (const item of (data || [])) {
        if (!item.companyId) continue
        if (!byCompany[item.companyId]) {
          byCompany[item.companyId] = { company: { id: item.companyId, name: item.companyName || item.companyId }, chains: [] }
        }
        byCompany[item.companyId].chains.push(item)
      }
      setGroups(Object.values(byCompany))
    } catch { notify?.({ type: 'error', message: 'Failed' }) }
    finally { setLoading(false) }
  }, [notify])

  useEffect(() => { loadAll() }, [loadAll])

  useEffect(() => {
    const t = setTimeout(async () => {
      try { const { data } = await flaskApi.get('/role-tree/companies/search', { params: { q } }); setCo(data || []) } catch {}
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  const activeCompany = groups.length === 1 ? groups[0].company : null

  useEffect(() => {
    if (!addingFor || !activeCompany) return
    (async () => {
      try {
        const { data } = await flaskApi.get('/role-tree/company-users', { params: { company_id: activeCompany.id } })
        setCompanyUsers(data || [])
      } catch { setCompanyUsers([]) }
    })()
  }, [addingFor, activeCompany])

  const users = companyUsers

  const removeApprover = async (requestId: number, chainId: string, groupCompanyId: string) => {
    try {
      await flaskApi.delete(`/approval-chains/${requestId}/remove`)
      setGroups(prev => prev.map(g => g.company.id === groupCompanyId ? {
        ...g, chains: g.chains.map(c => c.id === chainId
          ? { ...c, approvers: c.approvers.filter(a => a.id !== requestId) } : c)
      } : g))
      notify?.({ type: 'success', message: 'Approver removed' })
    } catch { notify?.({ type: 'error', message: 'Failed' }) }
  }

  const addApprover = async (chainId: string, companyUserId: string, groupCompanyId: string) => {
    try {
      const { data } = await flaskApi.post(`/approval-chains/${chainId}/add`, { user_id: companyUserId })
      setAddingFor(null)
      if (data && data.length > 0) {
        const updated = data[0]
        setGroups(prev => prev.map(g => g.company.id === groupCompanyId ? {
          ...g, chains: g.chains.map(c => c.id === chainId ? updated : c)
        } : g))
      }
      notify?.({ type: 'success', message: 'Approver added' })
    } catch { notify?.({ type: 'error', message: 'Failed' }) }
  }

  const toggleExpand = (id: string) => {
    setExpanded(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Approval Chains</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {groups.length === 1 ? groups[0].company.name : `${groups.length} companies`}
          </p>
        </div>
        <div className="relative">
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setSearchOpen(!searchOpen)}>
            <Search size={14} />Filter by company<ChevronDown size={12} />
          </Button>
          {searchOpen && (
            <div className="absolute right-0 top-full mt-1 w-80 bg-card rounded-xl border shadow-xl z-20 p-2">
              <input autoFocus className="w-full px-3 py-2 text-sm rounded-lg border bg-background outline-none focus:ring-2 focus:ring-ring"
                placeholder="Search companies..." value={q} onChange={e => setQ(e.target.value)} />
              <div className="mt-2 max-h-64 overflow-y-auto">
                <button className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-muted"
                  onClick={() => { loadAll(); setSearchOpen(false); setQ('') }}>
                  All companies
                </button>
                {co.map(c => (
                  <button key={c.id} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-muted"
                    onClick={() => { loadOne(c.id); setSearchOpen(false); setQ('') }}>
                    {c.name}<span className="ml-2 text-xs text-muted-foreground font-mono">{c.id.slice(0, 12)}...</span>
                  </button>
                ))}
                {!co.length && q && <p className="text-xs text-muted-foreground px-3 py-2">No results</p>}
              </div>
            </div>
          )}
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground text-center py-16">Loading...</p>}

      {!loading && groups.length === 0 && (
        <div className="rounded-xl border border-dashed p-16 text-center text-muted-foreground">
          <p className="text-sm">No requisitions with approval chains found</p>
        </div>
      )}

      {!loading && groups.map(group => (
        <div key={group.company.id}>
          {groups.length > 1 && (
            <h2 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-3 mt-6">
              {group.company.name}
            </h2>
          )}
          <div className="space-y-3">
            {group.chains.map(chain => {
              const isOpen = expanded.has(chain.id)
              return (
                <div key={chain.id} className="rounded-xl border bg-card overflow-hidden">
                  <button className="w-full flex items-center gap-4 px-5 py-3 text-left hover:bg-muted/30"
                    onClick={() => toggleExpand(chain.id)}>
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{chain.title || chain.id}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {chain.approvalCycleType || 'Direct'} - {chain.approvers.length} approvers - {chain.status}
                      </div>
                    </div>
                    <ChevronDown size={14} className={cn('text-muted-foreground transition-transform', isOpen && 'rotate-180')} />
                  </button>
                  {isOpen && (
                    <div className="border-t px-5 py-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Approvers</span>
                        <Button variant="outline" size="sm" className="text-xs gap-1 h-7 ml-auto"
                          onClick={() => setAddingFor(chain.id)}>
                          <Plus size={12} /> Add
                        </Button>
                      </div>
                      {addingFor === chain.id && (
                        <div className="mb-2 flex gap-2">
                          <select className="flex-1 text-xs rounded-lg border bg-background px-2 py-1"
                            onChange={e => { if (e.target.value) addApprover(chain.id, e.target.value, group.company.id) }}
                            value="">
                            <option value="">Select user...</option>
                            {users.map(u => (
                              <option key={u.id} value={u.userId}>{u.name}</option>
                            ))}
                          </select>
                          <Button variant="ghost" size="sm" className="text-xs h-7"
                            onClick={() => setAddingFor(null)}>Cancel</Button>
                        </div>
                      )}
                      <div className="divide-y">
                        {chain.approvers.map((a, i) => (
                          <div key={a.id} className="flex items-center gap-3 py-1.5">
                            <span className="text-xs text-muted-foreground w-5">{i + 1}</span>
                            <span className="text-xs flex-1">{a.userName}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{a.status}</span>
                            <button className="text-muted-foreground hover:text-destructive"
                              onClick={() => removeApprover(a.id, chain.id, group.company.id)}>
                              <Trash2 size={12} />
                            </button>
                          </div>
                        ))}
                        {chain.approvers.length === 0 && (
                          <p className="text-xs text-muted-foreground py-2">No approvers</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}
